import { ComponentChild } from 'preact'

import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import ButtonToggle from '@bonniernews/dn-design-system-web/components/button-toggle/button-toggle.tsx'
import Button from '@bonniernews/dn-design-system-web/components/button/button.tsx'
import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'

export type GroupHeaderType = 'icon' | 'link' | 'toggle' | 'arrows'

export interface GroupHeaderProps extends SharedProps {
  /** The title text */
  title: string
  /** The type of interactive element added to the header */
  type?: GroupHeaderType
  /** The design variant, if not bauta the group header will get the 'standard' design */
  variant?: 'bauta' | 'default'
  /** The image element for the header */
  media?: ComponentChild
  /** If given, the title is rendered as a link */
  href?: string
  /** The link text to render (only for types `link` and `arrows`) */
  linkText?: string
  /** If true there will be a bottom border */
  showBottomBorder?: boolean
  /** `text` parameter to ToggleButton (only for type `toggle`) */
  toggleText?: string
  /** `selectedText` parameter to ToggleButton (only for type `toggle`) */
  toggleSelectedText?: string
  /** `selected` parameter to ToggleButton (only for type `toggle`) */
  toggleSelected?: boolean
  /** `loading` parameter to ToggleButton (only for type `toggle`) */
  toggleLoading?: boolean
  /** `classNames` parameter to ToggleButton (only for type `toggle`) */
  toggleClassNames?: string
  /** `attributes` parameter to ToggleButton (only for type `toggle`) */
  toggleAttributes?: { [key: string]: string }
  /** Used to include or exclude elements based on channel */
  channel?: 'mobile' | 'tablet' | 'desktop'
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/group-header](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/group-header)
 * - Storybook: [GroupHeader](https://designsystem.dn.se/?path=/docs/basic-groupheader--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/group-header/group-header.scss'`
 */
export function GroupHeader({
  title,
  type = 'icon',
  variant,
  media,
  href,
  linkText,
  showBottomBorder = false,
  toggleText,
  toggleSelectedText,
  toggleSelected = false,
  toggleLoading = false,
  toggleClassNames,
  toggleAttributes = {},
  classNames,
  attributes = {},
  channel,
}: GroupHeaderProps) {
  const componentClassName = 'ds-group-header'

  // Reset variant if it is not 'bauta' since default is 'standard'
  variant = variant !== 'bauta' ? undefined : variant

  const classes = formatClassString([
    componentClassName,
    type && `${componentClassName}--${type}`,
    variant && `${componentClassName}--${variant}`,
    showBottomBorder && `${componentClassName}--bottom-border`,
    variant === 'bauta' && 'ds-dark',
    classNames,
  ])

  const groupHeaderInner = (
    <>
      {media && <div className={`${componentClassName}__image`}>{media}</div>}
      <h2 className={`${componentClassName}__title`}>{title}</h2>
      {type === 'link' && linkText && href && <span className={`${componentClassName}__right-link`}>{linkText}</span>}
      {type === 'icon' && href && <IconUse iconName='arrow_forward' classNames={`${componentClassName}__icon`} />}
      {type === 'arrows' && href && linkText && channel !== 'desktop' && (
        <span className={`${componentClassName}__right-link`}>{linkText}</span>
      )}
      {type === 'arrows' && href && !linkText && channel !== 'desktop' && (
        <IconUse iconName='arrow_forward' classNames={`${componentClassName}__icon`} />
      )}
    </>
  )

  const buttonVariant = variant ? 'staticWhite' : 'secondaryOutline'

  return (
    <div className={classes} {...attributes}>
      {href && (
        <a className={`${componentClassName}__href`} href={href}>
          {groupHeaderInner}
        </a>
      )}
      {!href && groupHeaderInner}

      {type === 'toggle' && !variant && (
        <ButtonToggle
          isIconButton={false}
          variant='primary'
          size='sm'
          text={toggleText}
          selectedText={toggleSelectedText || toggleText || ''}
          selected={toggleSelected}
          loading={toggleLoading}
          classNames={toggleClassNames}
          attributes={toggleAttributes}
        />
      )}

      {type === 'arrows' && channel === 'desktop' && (
        <div className={`${componentClassName}__arrows`}>
          <Button
            variant={buttonVariant}
            size='sm'
            isIconButton={true}
            iconName='arrow_back'
            classNames={`${componentClassName}__arrow ${componentClassName}__arrow--left`}
            attributes={{ 'aria-label': 'Föregående' }}
          />
          <Button
            variant={buttonVariant}
            size='sm'
            isIconButton={true}
            iconName='arrow_forward'
            classNames={`${componentClassName}__arrow ${componentClassName}__arrow--right`}
            attributes={{ 'aria-label': 'Nästa' }}
          />
        </div>
      )}
    </div>
  )
}
